import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from './firebase';
import { Receipt, CheckCircle, Clock, ArrowDownCircle, ArrowUpCircle, ExternalLink, Sparkles, ChevronLeft } from 'lucide-react';
import './SettlementHub.css';

// ── Types ─────────────────────────────────────────────────────────────────────

type Status = 'pending' | 'member_reported' | 'confirmed' | 'paid_out' | 'received';

interface Balance {
  balance: number;
  status: Status;
  paymentHandle?: string;
}

interface PublicSettlement {
  eventCode: string;
  eventName: string;
  paymentHandle: string;
  totalMembers: number;
  groupTotal: number;
  perPerson: number;
  balances: Record<string, Balance>;
  updatedAt: number;
  status?: 'open' | 'locked';
  lockedAt?: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number) => `$${Math.abs(n).toFixed(2)}`;

function getPaymentLink(handle: string, amount: number, note: string): string {
  if (!handle) return '';
  const h = handle.trim();
  if (h.startsWith('@')) {
    return `https://venmo.com/?txn=pay&audience=private&recipients=${encodeURIComponent(h.slice(1))}&amount=${amount.toFixed(2)}&note=${encodeURIComponent(note)}`;
  }
  if (h.startsWith('$')) {
    return `https://cash.app/${encodeURIComponent(h)}/${amount.toFixed(2)}`;
  }
  if (h.toLowerCase().includes('paypal.me')) {
    const base = h.startsWith('http') ? h : `https://${h}`;
    return `${base.replace(/\/$/, '')}/${amount.toFixed(2)}`;
  }
  if (h.includes('@') && !h.includes(' ')) {
    return `upi://pay?pa=${encodeURIComponent(h)}&am=${amount.toFixed(2)}&tn=${encodeURIComponent(note)}`;
  }
  return '';
}

function getPaymentLabel(handle: string): string {
  if (!handle) return 'Pay';
  const h = handle.trim();
  if (h.startsWith('@')) return 'Pay via Venmo';
  if (h.startsWith('$')) return 'Pay via Cash App';
  if (h.toLowerCase().includes('paypal')) return 'Pay via PayPal';
  if (h.includes('@')) return 'Pay via UPI';
  return 'Pay Now';
}

const reportPayment = httpsCallable<{ eventCode: string; memberName: string; action: string }, { success: boolean }>(
  functions, 'reportMemberPayment'
);

// ── Name matching helpers ──────────────────────────────────────────────────────

function charDistance(a: string, b: string): number {
  if (Math.abs(a.length - b.length) > 3) return 99;
  // Simple position-by-position diff (good enough for single-word names)
  let diff = Math.abs(a.length - b.length);
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    if (a[i] !== b[i]) diff++;
  }
  return diff;
}

function findCanonicalName(input: string, knownNames: string[]): string | null {
  const lower = input.toLowerCase().trim();
  return knownNames.find(n => n.toLowerCase() === lower) || null;
}

function findFuzzyMatch(input: string, knownNames: string[]): string | null {
  if (!input || input.length < 2) return null;
  const lower = input.toLowerCase().trim();
  for (const name of knownNames) {
    const nl = name.toLowerCase();
    if (nl === lower) return null; // exact match — no suggestion needed
    // prefix match (Sara / Sarah)
    if (nl.startsWith(lower) || lower.startsWith(nl)) return name;
    // close character distance (≤2 typos)
    if (charDistance(nl, lower) <= 2) return name;
  }
  return null;
}

// ── Ring component ─────────────────────────────────────────────────────────────

function ProgressRing({ pct }: { pct: number }) {
  const r = 54;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.min(1, pct));
  return (
    <svg width="130" height="130" className="sh-ring">
      <defs>
        <linearGradient id="sh-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#667eea" />
          <stop offset="100%" stopColor="#764ba2" />
        </linearGradient>
      </defs>
      <circle cx="65" cy="65" r={r} className="sh-ring-bg" />
      <circle
        cx="65" cy="65" r={r}
        className="sh-ring-fill"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        style={{ transition: 'stroke-dashoffset 1s ease' }}
      />
      <text x="65" y="60" textAnchor="middle" className="sh-ring-pct">
        {Math.round(pct * 100)}%
      </text>
      <text x="65" y="76" textAnchor="middle" className="sh-ring-lbl">
        settled
      </text>
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function SettlementHub() {
  const { eventCode } = useParams<{ eventCode: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const memberNameFromUrl = searchParams.get('name') || '';

  const [user, setUser] = useState<User | null>(null);
  const [settlement, setSettlement] = useState<PublicSettlement | null>(null);
  const [loading, setLoading] = useState(true);
  const [myName, setMyName] = useState(memberNameFromUrl);
  const [nameInput, setNameInput] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [acting, setActing] = useState<string | null>(null);
  const [paymentInitiated, setPaymentInitiated] = useState<Set<string>>(new Set());
  const [adminConfirmName, setAdminConfirmName] = useState('');
  const [adminConfirming, setAdminConfirming] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);

  // Auth (to detect if admin)
  useEffect(() => {
    return onAuthStateChanged(auth, u => setUser(u));
  }, []);

  // Restore payment-initiated state from sessionStorage (survives Venmo tab-switch)
  useEffect(() => {
    if (!eventCode) return;
    const code = eventCode.toUpperCase();
    const initiated = new Set<string>();
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(`xpm_paid_${code}_`)) {
        initiated.add(key.replace(`xpm_paid_${code}_`, ''));
      }
    }
    if (initiated.size > 0) setPaymentInitiated(initiated);
  }, [eventCode]);

  // Live settlement subscription
  useEffect(() => {
    if (!eventCode) return;
    const code = eventCode.toUpperCase();
    return onSnapshot(doc(db, 'eventSettlements', code), snap => {
      if (snap.exists()) setSettlement(snap.data() as PublicSettlement);
      setLoading(false);
    }, () => setLoading(false));
  }, [eventCode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (autocompleteRef.current && !autocompleteRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="sh-page">
        <div className="sh-center"><div className="sh-spinner" /></div>
      </div>
    );
  }

  if (!settlement) {
    return (
      <div className="sh-page">
        <div className="sh-center">
          <p className="sh-msg">Settlement not available yet. The organizer needs to open the event first.</p>
        </div>
      </div>
    );
  }

  // ── Computed ──────────────────────────────────────────────────────────────

  const code = settlement.eventCode;
  const allKnownNames = Object.keys(settlement.balances);
  const payerEntries = Object.entries(settlement.balances).filter(([, b]) => b.balance < -0.01);
  const receiverEntries = Object.entries(settlement.balances).filter(([, b]) => b.balance > 0.01);

  // Members who haven't submitted any receipts yet — they owe the per-person share
  const knownNames = new Set(allKnownNames);
  const pendingCount = Math.max(0, settlement.totalMembers - knownNames.size);

  // Resolve myName to canonical spelling (case-insensitive + fuzzy)
  const canonicalMyName = myName
    ? (findCanonicalName(myName, allKnownNames) || myName)
    : '';
  // Use canonical for all balance lookups
  const effectiveMyName = canonicalMyName;

  // If resolved name is not in balances, they are a non-submitting member
  const myNameIsNonSubmitter = effectiveMyName && !knownNames.has(effectiveMyName);

  // Autocomplete: filter known names by what's typed, starts-with first
  const inputTrimmed = nameInput.trim().toLowerCase();
  const dropdownSuggestions = inputTrimmed.length >= 1
    ? allKnownNames
        .filter(n => n.toLowerCase().includes(inputTrimmed))
        .sort((a, b) => {
          const aS = a.toLowerCase().startsWith(inputTrimmed);
          const bS = b.toLowerCase().startsWith(inputTrimmed);
          if (aS && !bS) return -1;
          if (!aS && bS) return 1;
          return a.localeCompare(b);
        })
    : [];

  function pickName(name: string) {
    setMyName(name);
    setNameInput('');
    setShowDropdown(false);
  }

  function confirmTypedName() {
    const val = nameInput.trim();
    if (!val) return;
    // If exact canonical match exists, use that spelling
    const canonical = findCanonicalName(val, allKnownNames);
    setMyName(canonical || val);
    setNameInput('');
    setShowDropdown(false);
  }

  const settledPayers = payerEntries.filter(([, b]) => b.status === 'confirmed').length;
  const settledReceivers = receiverEntries.filter(([, b]) => b.status === 'received').length;
  const totalPeople = payerEntries.length + receiverEntries.length;
  const settledPeople = settledPayers + settledReceivers;
  const progressPct = totalPeople > 0 ? settledPeople / totalPeople : 0;

  const collected = payerEntries
    .filter(([, b]) => b.status === 'confirmed')
    .reduce((s, [, b]) => s + Math.abs(b.balance), 0);

  const distributed = receiverEntries
    .filter(([, b]) => b.status === 'received')
    .reduce((s, [, b]) => s + b.balance, 0);

  const poolBalance = collected - distributed;
  const isFullySettled = settledPeople === totalPeople && totalPeople > 0;

  const note = `${settlement.eventName} (${code})`;

  // ── Admin actions (direct Firestore write) ──────────────────────────────

  async function adminConfirmPaidIn(name: string) {
    setActing(name);
    await updateDoc(doc(db, 'eventSettlements', code), {
      [`balances.${name}.status`]: 'confirmed',
      updatedAt: Date.now(),
    });
    setActing(null);
  }

  async function adminConfirmManual(name: string) {
    if (!name.trim()) return;
    setAdminConfirming(true);
    const existing = settlement!.balances[name];
    if (existing) {
      await updateDoc(doc(db, 'eventSettlements', code), {
        [`balances.${name}.status`]: 'confirmed',
        updatedAt: Date.now(),
      });
    } else {
      await updateDoc(doc(db, 'eventSettlements', code), {
        [`balances.${name}`]: {
          balance: -(settlement!.perPerson),
          status: 'confirmed',
        },
        updatedAt: Date.now(),
      });
    }
    setAdminConfirmName('');
    setAdminConfirming(false);
  }

  async function adminMarkPaidOut(name: string) {
    setActing(name);
    await updateDoc(doc(db, 'eventSettlements', code), {
      [`balances.${name}.status`]: 'paid_out',
      updatedAt: Date.now(),
    });
    setActing(null);
  }

  // ── Member actions (Cloud Function) ────────────────────────────────────

  async function memberIPaid(name: string) {
    setActing(name);
    try {
      await reportPayment({ eventCode: code, memberName: name, action: 'paid_in' });
    } catch (e) { console.error(e); }
    setActing(null);
  }

  async function memberGotIt(name: string) {
    setActing(name);
    try {
      await reportPayment({ eventCode: code, memberName: name, action: 'received' });
    } catch (e) { console.error(e); }
    setActing(null);
  }

  // ── Status helpers ──────────────────────────────────────────────────────

  function statusBadge(status: Status, isPayer: boolean) {
    if (isPayer) {
      if (status === 'confirmed') return <span className="sh-badge green"><CheckCircle size={12} /> Confirmed</span>;
      if (status === 'member_reported') return <span className="sh-badge yellow"><Clock size={12} /> Payment reported</span>;
      return <span className="sh-badge grey"><Clock size={12} /> Pending</span>;
    } else {
      if (status === 'received') return <span className="sh-badge green"><CheckCircle size={12} /> Received</span>;
      if (status === 'paid_out') return <span className="sh-badge blue"><ArrowUpCircle size={12} /> Payment sent</span>;
      return <span className="sh-badge grey"><Clock size={12} /> Awaiting</span>;
    }
  }

  return (
    <div className="sh-page">
      {/* Header */}
      <header className="sh-header">
        <div className="sh-header-left">
          <button className="sh-back" onClick={() => navigate('/')}>
            <ChevronLeft size={16} /> Home
          </button>
          <div className="sh-logo">
            <div className="sh-logo-icon"><Receipt size={16} color="white" strokeWidth={2} /></div>
            <span className="sh-logo-text">XpenseMate</span>
          </div>
        </div>
        <span className="sh-code-badge">{code}</span>
      </header>

      <main className="sh-main">
        <h1 className="sh-title">{settlement.eventName}</h1>
        <p className="sh-subtitle">{settlement.totalMembers} members · {fmt(settlement.perPerson)}/person</p>

        {/* Open / locked status banner */}
        {settlement.status !== 'locked' ? (
          <div className="sh-open-banner">
            <span className="sh-open-dot" />
            <span>Receipts still being collected — balances will change as more are submitted.</span>
          </div>
        ) : (
          <div className="sh-locked-banner">
            🔒 <strong>Submissions closed.</strong> These balances are final.
          </div>
        )}

        {/* Who are you? (member identification) */}
        {!user && !myName && (
          <div className="sh-identify">
            <p className="sh-identify-label">Who are you?</p>
            <div className="sh-autocomplete" ref={autocompleteRef}>
              <div className="sh-identify-row">
                <input
                  className="sh-input-name"
                  placeholder="Type your name…"
                  value={nameInput}
                  autoComplete="off"
                  onChange={e => { setNameInput(e.target.value); setShowDropdown(true); }}
                  onFocus={() => { if (nameInput.trim().length >= 1) setShowDropdown(true); }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') confirmTypedName();
                    if (e.key === 'Escape') setShowDropdown(false);
                  }}
                />
                <button
                  className="sh-btn-identify"
                  disabled={!nameInput.trim()}
                  onClick={confirmTypedName}
                >
                  OK
                </button>
              </div>
              {showDropdown && dropdownSuggestions.length > 0 && (
                <ul className="sh-dropdown">
                  {dropdownSuggestions.map(n => {
                    const b = settlement.balances[n];
                    return (
                      <li key={n} className="sh-dropdown-item" onMouseDown={() => pickName(n)}>
                        <span className="sh-dropdown-name">{n}</span>
                        {b && (
                          <span className={`sh-dropdown-hint ${b.balance < -0.01 ? 'owes' : 'gets'}`}>
                            {b.balance < -0.01 ? `owes ${fmt(b.balance)}` : `gets back ${fmt(b.balance)}`}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Progress ring */}
        <div className="sh-pool-card">
          <div className="sh-ring-wrap">
            <ProgressRing pct={progressPct} />
          </div>
          <div className="sh-pool-stats">
            <div className="sh-pool-stat">
              <span className="sh-pool-val">{fmt(settlement.groupTotal)}</span>
              <span className="sh-pool-lbl">total pool</span>
            </div>
            <div className="sh-pool-stat accent">
              <span className="sh-pool-val">{fmt(collected)}</span>
              <span className="sh-pool-lbl">collected</span>
            </div>
            <div className="sh-pool-stat">
              <span className="sh-pool-val">{fmt(distributed)}</span>
              <span className="sh-pool-lbl">distributed</span>
            </div>
            <div className={`sh-pool-stat ${poolBalance < 0.01 && collected > 0 ? 'zero' : 'pending-val'}`}>
              <span className="sh-pool-val">{fmt(poolBalance)}</span>
              <span className="sh-pool-lbl">pool balance</span>
            </div>
          </div>
        </div>

        {/* Fully settled celebration */}
        {isFullySettled && (
          <div className="sh-settled-banner">
            <Sparkles size={22} />
            <div>
              <strong>Fully settled!</strong>
              <span> Pool balance: $0.00 · Everyone is square.</span>
            </div>
          </div>
        )}

        {/* Paying In */}
        <section className="sh-section">
          <div className="sh-section-header">
            <ArrowDownCircle size={16} className="sh-section-icon in" />
            <h2 className="sh-section-title">Paying in</h2>
            <span className="sh-section-count">{settledPayers}/{payerEntries.length + (myNameIsNonSubmitter ? 1 : 0) + pendingCount} confirmed</span>
          </div>
          <div className="sh-rows">

            {/* Non-submitting member who typed their name */}
            {myNameIsNonSubmitter && settlement.perPerson > 0.01 && (
              <div className="sh-row mine">
                <div className="sh-row-left">
                  <div className="sh-avatar">{effectiveMyName[0].toUpperCase()}</div>
                  <div className="sh-row-info">
                    <span className="sh-row-name">{effectiveMyName}<span className="sh-you"> you</span></span>
                    <span className="sh-row-amount owes">{fmt(settlement.perPerson)} to pay</span>
                  </div>
                </div>
                <div className="sh-row-right">
                  <span className="sh-badge grey"><Clock size={12} /> Pending</span>
                  {settlement.paymentHandle && (() => {
                    const payLink = getPaymentLink(settlement.paymentHandle, settlement.perPerson, note);
                    const payLabel = getPaymentLabel(settlement.paymentHandle);
                    const didInitiate = paymentInitiated.has(effectiveMyName);
                    function handlePayTap() {
                      const key = `xpm_paid_${code}_${effectiveMyName}`;
                      sessionStorage.setItem(key, '1');
                      setPaymentInitiated(prev => { const s = new Set(prev); s.add(effectiveMyName); return s; });
                    }
                    return didInitiate ? (
                      <div className="sh-confirm-prompt">
                        {payLink && (
                          <a href={payLink} target="_blank" rel="noreferrer"
                             className="sh-btn-pay small" onClick={handlePayTap}>
                            <ExternalLink size={11} /> Resend
                          </a>
                        )}
                        <button className="sh-btn-ipaid sh-btn-ipaid--prominent"
                          disabled={acting === effectiveMyName}
                          onClick={() => memberIPaid(effectiveMyName)}>
                          {acting === effectiveMyName ? '…' : "✓ I've sent it"}
                        </button>
                      </div>
                    ) : (
                      <>
                        {payLink && (
                          <a href={payLink} target="_blank" rel="noreferrer"
                             className="sh-btn-pay" onClick={handlePayTap}>
                            <ExternalLink size={12} /> {payLabel}
                          </a>
                        )}
                        <button className="sh-btn-ipaid" disabled={acting === effectiveMyName}
                          onClick={() => memberIPaid(effectiveMyName)}>
                          {acting === effectiveMyName ? '…' : 'I paid ✓'}
                        </button>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {payerEntries.sort((a, b) => a[0].localeCompare(b[0])).map(([name, b]) => {
              const isMe = name === effectiveMyName;
              const payLink = getPaymentLink(settlement.paymentHandle, Math.abs(b.balance), note);
              const payLabel = getPaymentLabel(settlement.paymentHandle);
              const isWaiting = acting === name;
              const didInitiate = paymentInitiated.has(name);

              function handlePayTap() {
                const key = `xpm_paid_${code}_${name}`;
                sessionStorage.setItem(key, '1');
                setPaymentInitiated(prev => { const s = new Set(prev); s.add(name); return s; });
              }

              // How much they contributed via receipts (partial payers)
              const contributed = settlement.perPerson + b.balance; // perPerson - |balance|
              const isPartialPayer = contributed > 0.01;

              return (
                <div key={name} className={`sh-row${isMe ? ' mine' : ''}${b.status === 'confirmed' ? ' done' : ''}`}>
                  <div className="sh-row-left">
                    <div className="sh-avatar">{name[0].toUpperCase()}</div>
                    <div className="sh-row-info">
                      <span className="sh-row-name">{name}{isMe && <span className="sh-you"> you</span>}</span>
                      <span className="sh-row-amount owes">{fmt(b.balance)} to pay</span>
                      {isPartialPayer && (
                        <span className="sh-row-breakdown">
                          {fmt(settlement.perPerson)} share − {fmt(contributed)} paid via receipts
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="sh-row-right">
                    {statusBadge(b.status, true)}

                    {/* Member: not yet paid */}
                    {!user && b.status === 'pending' && isMe && (
                      didInitiate ? (
                        /* Sticky prompt after returning from payment app */
                        <div className="sh-confirm-prompt">
                          {payLink && (
                            <a href={payLink} target="_blank" rel="noreferrer"
                               className="sh-btn-pay small" onClick={handlePayTap}>
                              <ExternalLink size={11} /> Resend
                            </a>
                          )}
                          <button className="sh-btn-ipaid sh-btn-ipaid--prominent"
                            disabled={isWaiting} onClick={() => memberIPaid(name)}>
                            {isWaiting ? '…' : "✓ I've sent it"}
                          </button>
                        </div>
                      ) : (
                        <>
                          {payLink && (
                            <a href={payLink} target="_blank" rel="noreferrer"
                               className="sh-btn-pay" onClick={handlePayTap}>
                              <ExternalLink size={12} /> {payLabel}
                            </a>
                          )}
                          <button className="sh-btn-ipaid" disabled={isWaiting}
                            onClick={() => memberIPaid(name)}>
                            {isWaiting ? '…' : 'I paid ✓'}
                          </button>
                        </>
                      )
                    )}

                    {/* Admin: confirm received */}
                    {user && (b.status === 'member_reported' || b.status === 'pending') && (
                      <>
                        {payLink && b.status === 'pending' && (
                          <a href={payLink} target="_blank" rel="noreferrer" className="sh-btn-pay small">
                            <ExternalLink size={11} /> Send link
                          </a>
                        )}
                        <button className="sh-btn-confirm" disabled={isWaiting}
                          onClick={() => adminConfirmPaidIn(name)}>
                          {isWaiting ? '…' : b.status === 'member_reported' ? 'Confirm ✓' : 'Mark received'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Pending members who haven't submitted receipts yet */}
            {pendingCount > 0 && !myNameIsNonSubmitter && (
              <>
                <div className="sh-row sh-row-pending">
                  <div className="sh-row-left">
                    <div className="sh-avatar pending">+{pendingCount}</div>
                    <div className="sh-row-info">
                      <span className="sh-row-name">{pendingCount} member{pendingCount > 1 ? 's' : ''} still owe</span>
                      <span className="sh-row-amount owes">{fmt(settlement.perPerson)} each to pay</span>
                    </div>
                  </div>
                  <div className="sh-row-right">
                    <span className="sh-badge grey"><Clock size={12} /> Awaiting</span>
                  </div>
                </div>

                {/* Inline "that's me" prompt — only show when not already identified */}
                {!myName && (
                  <div className="sh-itsme-box">
                    <p className="sh-itsme-label">
                      👋 Are you one of these? Enter your name to see your balance and pay.
                    </p>
                    <div className="sh-autocomplete">
                      <div className="sh-identify-row">
                        <input
                          className="sh-input-name"
                          placeholder="Your name…"
                          value={nameInput}
                          autoComplete="off"
                          onChange={e => { setNameInput(e.target.value); setShowDropdown(true); }}
                          onFocus={() => { if (nameInput.trim().length >= 1) setShowDropdown(true); }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') confirmTypedName();
                            if (e.key === 'Escape') setShowDropdown(false);
                          }}
                        />
                        <button
                          className="sh-btn-identify"
                          disabled={!nameInput.trim()}
                          onClick={confirmTypedName}
                        >
                          That's me →
                        </button>
                      </div>
                      {showDropdown && dropdownSuggestions.length > 0 && (
                        <ul className="sh-dropdown">
                          {dropdownSuggestions.map(n => {
                            const b = settlement.balances[n];
                            return (
                              <li key={n} className="sh-dropdown-item" onMouseDown={() => pickName(n)}>
                                <span className="sh-dropdown-name">{n}</span>
                                {b && (
                                  <span className={`sh-dropdown-hint ${b.balance < -0.01 ? 'owes' : 'gets'}`}>
                                    {b.balance < -0.01 ? `owes ${fmt(b.balance)}` : `gets back ${fmt(b.balance)}`}
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Getting Back */}
        <section className="sh-section">
          <div className="sh-section-header">
            <ArrowUpCircle size={16} className="sh-section-icon out" />
            <h2 className="sh-section-title">Getting back</h2>
            <span className="sh-section-count">{settledReceivers}/{receiverEntries.length} received</span>
          </div>
          <div className="sh-rows">
            {receiverEntries.sort((a, b) => b[1].balance - a[1].balance).map(([name, b]) => {
              const isMe = name === effectiveMyName;
              const isWaiting = acting === name;
              const payoutLink = b.paymentHandle
                ? getPaymentLink(b.paymentHandle, b.balance, note)
                : '';

              return (
                <div key={name} className={`sh-row${isMe ? ' mine' : ''}${b.status === 'received' ? ' done' : ''}`}>
                  <div className="sh-row-left">
                    <div className="sh-avatar gets">{name[0].toUpperCase()}</div>
                    <div className="sh-row-info">
                      <span className="sh-row-name">{name}{isMe && <span className="sh-you"> you</span>}</span>
                      <span className="sh-row-amount gets">{fmt(b.balance)} to receive</span>
                      {b.paymentHandle && (
                        <span className="sh-handle-tag">{b.paymentHandle}</span>
                      )}
                    </div>
                  </div>
                  <div className="sh-row-right">
                    {statusBadge(b.status, false)}
                    {/* Admin: open their payment app pre-filled, then mark paid out */}
                    {user && b.status === 'pending' && (
                      <button
                        className="sh-btn-payout"
                        disabled={isWaiting}
                        onClick={async () => {
                          if (payoutLink) window.open(payoutLink, '_blank');
                          await adminMarkPaidOut(name);
                        }}
                      >
                        {isWaiting ? '…' : payoutLink ? `Pay ${fmt(b.balance)} →` : `Mark paid →`}
                      </button>
                    )}
                    {/* Member: confirm receipt */}
                    {!user && b.status === 'paid_out' && isMe && (
                      <button className="sh-btn-gotit" disabled={isWaiting}
                        onClick={() => memberGotIt(name)}>
                        {isWaiting ? '…' : 'Got it ✓'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Payment handle info */}
        {settlement.paymentHandle && (
          <p className="sh-handle-note">
            Payments go to <strong>{settlement.paymentHandle}</strong>
          </p>
        )}

        {/* Admin: manually mark any member as paid */}
        {user && pendingCount > 0 && (
          <div className="sh-admin-confirm-box">
            <p className="sh-admin-confirm-title">Mark someone as paid</p>
            <p className="sh-admin-confirm-hint">
              Once you receive a real payment (Venmo, cash, etc.), enter the member's name and confirm.
            </p>
            <div className="sh-identify-row">
              <input
                className="sh-input-name"
                placeholder="Member name…"
                value={adminConfirmName}
                onChange={e => setAdminConfirmName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && adminConfirmName.trim()) adminConfirmManual(adminConfirmName.trim()); }}
              />
              <button
                className="sh-btn-identify sh-btn-confirm-green"
                disabled={!adminConfirmName.trim() || adminConfirming}
                onClick={() => adminConfirmManual(adminConfirmName.trim())}
              >
                {adminConfirming ? '…' : 'Confirm paid ✓'}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

